// Street Interview Ad's media row (live's `media-row`, captures 132 to 141 and 172 to 174): the
// product (one of the brand's, or a packshot uploaded with its name and text), then the cast,
// Auto (the director casts all five) or customised (the interviewer and up to four people).
import {
  ActionIcon,
  Button,
  CloseButton,
  Group,
  Image,
  Loader,
  Menu,
  Modal,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  UnstyledButton,
  VisuallyHidden,
} from "@mantine/core";
import { Archive, ArrowRight, ImageOff, LibraryBig, RotateCcw, SmilePlus, Sparkles, Upload, UsersRound } from "lucide-react";
import { useRef, useState } from "react";

import type { ActorSource, ExampleRef, PackshotProduct, UploadedPackshot } from "../../../api/generated/model";
import { AddActorsDialog } from "../../../platform/actors/AddActorsDialog";
import type { PickedActor } from "../../../platform/actors/picked";
import { pictureOf } from "../../../platform/media/ImagePicker";
import { IMAGE_ONLY, UploadState, useMediaUpload } from "../../../platform/media/media-upload";
import { ProductPicker } from "../../../platform/media/ProductPicker";
import { useRefPicture } from "../../../platform/media/ref-picture";
import { FieldLabel } from "../../../ui/FieldLabel";
import { sourceKey, useProductPick, type PickedPictures } from "./PodcastTiles";
import { PICK_PRODUCT } from "./street-script";
import classes from "./podcast.module.css";

/** A template's words on its product tile and its Add Product modal: the tile's tooltip, the
 * name's and the text's (each template's `library-view/constants.ts`). */
export interface ProductWords {
  tile: string;
  name: string;
  text: string;
}

/** Live's words (`library-view/constants.ts`, `packshot-product-tile`, `add-product-modal`). */
const STREET_PRODUCT_WORDS: ProductWords = {
  tile:
    "The product and the exact photo of it the ad is built on. Pick one of your products and its " +
    "packshot, or upload a packshot — the pack people hold in every shot.",
  name:
    "The product name the interviewer says and the end card shows. Leave empty and it is read off " +
    "the packshot.",
  text:
    "A description or the product page. Every claim in the script is quoted from it — leave empty " +
    "and the words printed on the pack are used.",
};
export const CAST_TOOLTIP =
  "The director casts the interviewer and four people from your product's buyers — it gives the " +
  "best results. Customise the cast to pick an AI Actor or a photo for anyone; whoever is left is " +
  "cast by the director.";
export const INTERVIEWER_TOOLTIP =
  "The interviewer who asks the question and hands over the product. Pick an AI Actor or upload " +
  "a photo — leave empty and the director casts them.";
const PEOPLE_TOOLTIP =
  "Up to four people stopped on the street, answering in the order picked. Pick AI Actors or " +
  "photos — the director casts whoever is left.";

/** The typed texts of an uploaded packshot (live's `productName`, `productText`). */
export interface TypedProduct {
  product_name: string;
  product_text: string;
}

export interface StreetProductTileProps {
  teamId: string;
  brandId: string;
  pick: PackshotProduct | null;
  typed: TypedProduct;
  disabled: boolean;
  /** Live's "Pick a product and its packshot", once Generate Script asked for one. */
  missing: boolean;
  /** The card's example inputs' URLs by key: the example's packshot is shown from them. */
  exampleUrls: Readonly<Record<string, string>>;
  /** A new pick, with the typed texts it carries (a catalogue pick clears them, as live's). */
  onChange: (pick: PackshotProduct | null, typed: TypedProduct) => void;
  /** The template's words on the tile and the modal; Street Interview Ad's when none. */
  words?: ProductWords;
}

/** A packshot added by hand: an upload, or the card's example packshot (live's "Reuse example"). */
type HandPick = UploadedPackshot | ExampleRef;

function isHandPick(pick: PackshotProduct | null): pick is HandPick {
  return pick !== null && pick.kind !== "product";
}

/**
 * Live's `PackshotProductTile`: empty, the archive box and "Product" with live's tooltip, whose
 * menu is "From products" (a product then its packshot) and "Upload packshot" (live's Add Product
 * modal: the photo, the name and the text). Filled, the packshot with an x.
 */
export function StreetProductTile(props: StreetProductTileProps) {
  const { teamId, brandId, pick, typed, disabled, missing, exampleUrls, onChange, words = STREET_PRODUCT_WORDS } = props;
  const [picking, setPicking] = useState(false);
  const [adding, setAdding] = useState(false);
  const catalogue =
    pick?.kind === "product" ? { product_id: pick.product_id, image_id: pick.image_id } : null;
  const shown = useProductPick(teamId, brandId, catalogue);
  const uploaded = useRefPicture(
    teamId,
    brandId,
    pick?.kind === "upload" ? { kind: "asset", asset_id: pick.asset_id } : null,
  );
  let picture = shown.url;
  if (pick?.kind === "upload") picture = uploaded.still;
  if (pick?.kind === "example") picture = exampleUrls[pick.key] ?? null;
  const name = isHandPick(pick) ? typed.product_name : shown.name;
  return (
    <div className={classes.tileField}>
      <ProductPicker
        teamId={teamId}
        brandId={brandId}
        chosen={
          catalogue === null
            ? null
            : { productId: catalogue.product_id, assetId: catalogue.image_id, url: shown.url, name: shown.name }
        }
        subtitle="You can select up to 1 products"
        opened={picking}
        onClose={() => setPicking(false)}
        onConfirm={(choice) =>
          onChange(
            { kind: "product", product_id: choice.productId, image_id: choice.assetId },
            { product_name: "", product_text: "" },
          )
        }
        anchor={(wrap) =>
          pick === null ? (
            <Menu position="bottom-start" disabled={disabled}>
              <Tooltip label={words.tile} multiline maw={280} withArrow disabled={picking || adding}>
                {wrap(
                  <Menu.Target>
                    <UnstyledButton className={classes.tile} aria-label="Product" disabled={disabled}>
                      <Archive size={20} strokeWidth={1.5} aria-hidden />
                      <span>Product</span>
                    </UnstyledButton>
                  </Menu.Target>,
                )}
              </Tooltip>
              <Menu.Dropdown>
                <Menu.Item leftSection={<LibraryBig size={16} strokeWidth={1.5} />} onClick={() => setPicking(true)}>
                  From products
                </Menu.Item>
                <Menu.Item leftSection={<Upload size={16} strokeWidth={1.5} />} onClick={() => setAdding(true)}>
                  Upload packshot
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          ) : (
            <span className={classes.tileFilled}>
              {wrap(
                <UnstyledButton
                  className={classes.tilePicture}
                  aria-label={`Product: ${name || "the packshot"}`}
                  disabled={disabled}
                  onClick={() => (isHandPick(pick) ? setAdding(true) : setPicking(true))}
                >
                  {picture === null ? null : <Image src={picture} alt="" className={classes.tilePicture} />}
                </UnstyledButton>,
              )}
              {disabled ? null : (
                <CloseButton
                  size="xs"
                  className={classes.tileRemove}
                  aria-label="Remove the product"
                  onClick={() => onChange(null, { product_name: "", product_text: "" })}
                />
              )}
            </span>
          )
        }
      />
      {missing && pick === null ? <Text className={classes.fieldError}>{PICK_PRODUCT}</Text> : null}
      {adding ? (
        <AddProductModal
          teamId={teamId}
          brandId={brandId}
          words={words}
          start={isHandPick(pick) ? { pick, picture, ...typed } : null}
          onClose={() => setAdding(false)}
          onConfirm={(packshot, texts) => {
            onChange(packshot, texts);
            setAdding(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface AddProductModalProps {
  teamId: string;
  brandId: string;
  words: ProductWords;
  start: ({ pick: HandPick; picture: string | null } & TypedProduct) | null;
  onClose: () => void;
  onConfirm: (pick: HandPick, typed: TypedProduct) => void;
}

/** Live's Add Product modal (captures 172 to 174): the photo (Upload Images), the name and the
 * text, each optional but the photo; Confirm and Cancel. The example's packshot stays until a
 * photo is uploaded in its place. */
function AddProductModal({ teamId, brandId, words, start, onClose, onConfirm }: AddProductModalProps) {
  const [photo, setPhoto] = useState<{ pick: HandPick; url: string | null } | null>(
    start === null ? null : { pick: start.pick, url: start.picture },
  );
  const [name, setName] = useState(start?.product_name ?? "");
  const [text, setText] = useState(start?.product_text ?? "");
  const input = useRef<HTMLInputElement>(null);
  const upload = useMediaUpload({
    teamId,
    brandId,
    kinds: IMAGE_ONLY,
    onUploaded: (asset) => setPhoto({ pick: { kind: "upload", asset_id: asset.id }, url: pictureOf(asset) }),
    onUploading: () => undefined,
  });
  return (
    <Modal opened onClose={onClose} title="Add Product" centered>
      <Stack gap={12}>
        <Stack gap={6}>
          <Text size="sm" fw={500}>
            Product Photo
          </Text>
          {photo === null ? null : photo.url === null ? (
            <Loader size="sm" aria-label="The packshot" />
          ) : (
            <Image src={photo.url} alt="The packshot" className={classes.tilePicture} />
          )}
          <input
            ref={input}
            type="file"
            accept="image/*"
            hidden
            aria-label="Product Photo"
            onChange={(event) => void upload.send(event.currentTarget.files?.[0] ?? null)}
          />
          <Button variant="default" disabled={upload.pending} onClick={() => input.current?.click()}>
            Upload Images
          </Button>
          <UploadState upload={upload} />
        </Stack>
        <TextInput
          label={<FieldLabel label="Product Name" info={words.name} />}
          placeholder="Add product name"
          maxLength={200}
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
        />
        <Textarea
          label={<FieldLabel label="Product Text" info={words.text} />}
          placeholder="A description or the product page"
          autosize
          minRows={3}
          maxRows={8}
          maxLength={20_000}
          value={text}
          onChange={(event) => setText(event.currentTarget.value)}
        />
        <Group justify="flex-end" gap={8}>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={photo === null || upload.pending}
            onClick={() => {
              if (photo !== null) onConfirm(photo.pick, { product_name: name, product_text: text });
            }}
          >
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Live's Auto cast tile (`AutoCastTile`, captures 132 and 134) and, to its right in the tiles' row,
 * its "Customise cast →" (`media-row`'s `castLink`).
 */
export function AutoCastTile({ disabled, onCustomise }: { disabled: boolean; onCustomise: () => void }) {
  return (
    <>
      <Tooltip label={CAST_TOOLTIP} multiline maw={280} withArrow>
        <span className={classes.tile} aria-label="Auto cast">
          <Sparkles size={20} strokeWidth={1.5} aria-hidden />
          <span>Auto cast</span>
          <Text size="xs" c="dimmed">
            AI picks interviewer &amp; guests
          </Text>
        </span>
      </Tooltip>
      <Button
        variant="subtle"
        size="compact-sm"
        className={classes.castLink}
        disabled={disabled}
        rightSection={<ArrowRight size={16} strokeWidth={1.5} />}
        onClick={onCustomise}
      >
        Customise cast
      </Button>
    </>
  );
}

/** The four people's seats, P1 to P4 (live's `cast.p1` to `cast.p4`). */
export type Seat = "p1" | "p2" | "p3" | "p4";
export const SEATS: readonly Seat[] = ["p1", "p2", "p3", "p4"];

export interface PeopleTileProps {
  teamId: string;
  brandId: string;
  seats: Readonly<Record<Seat, ActorSource | null>>;
  /** The seats an AI Actor is being made for. */
  making: readonly Seat[];
  /** The seats whose AI Actor failed: shown failed until removed. */
  failed: readonly Seat[];
  pictures: PickedPictures;
  disabled: boolean;
  onMakeActor: ((seat: Seat) => void) | null;
  /** A failed seat's AI Actor made again (the composer reopened for it); null with no maker. */
  onRetry: ((seat: Seat) => void) | null;
  onPick: (picks: readonly { seat: Seat; actor: PickedActor }[]) => void;
  onRemove: (seat: Seat) => void;
}

/**
 * Live's People tile (`people-tile-field`, `people-collage`, `people-cell`; captures 138 to 141):
 * up to four people; its menu "From Library" fills the free seats in seat order (as many as are
 * free) and "Add AI Actor" the first free seat; the menu is off when all four are taken. Filled,
 * a collage that opens the menu, each person with its own "Remove person" (hidden while its AI
 * Actor is made; a failed one shows `ImageOff`), which stays usable with every seat taken. A
 * removed seat stays empty until the next pick fills it (live's B13).
 */
export function PeopleTile(props: PeopleTileProps) {
  const { teamId, brandId, seats, making, failed, pictures, disabled, onMakeActor, onRetry, onPick, onRemove } = props;
  const [choosing, setChoosing] = useState(false);
  const busy = (seat: Seat) => making.includes(seat) || failed.includes(seat);
  const free = SEATS.filter((seat) => seats[seat] === null && !busy(seat));
  const taken = SEATS.filter((seat) => seats[seat] !== null || busy(seat));
  const menuOff = disabled || free.length === 0;
  const dropdown = (
    <Menu.Dropdown>
      <Menu.Item leftSection={<LibraryBig size={16} strokeWidth={1.5} />} onClick={() => setChoosing(true)}>
        From Library
      </Menu.Item>
      <Menu.Item
        leftSection={<UsersRound size={16} strokeWidth={1.5} />}
        disabled={onMakeActor === null || free[0] === undefined}
        onClick={() => {
          const seat = free[0];
          if (seat !== undefined && onMakeActor !== null) onMakeActor(seat);
        }}
      >
        Add AI Actor
      </Menu.Item>
    </Menu.Dropdown>
  );
  return (
    <div className={classes.tileField}>
      {taken.length === 0 ? (
        <Menu position="bottom-start" disabled={menuOff}>
          <Tooltip label={PEOPLE_TOOLTIP} multiline maw={280} withArrow position="right">
            <Menu.Target>
              <UnstyledButton className={classes.tile} aria-label="People" disabled={menuOff}>
                <SmilePlus size={20} strokeWidth={1.5} aria-hidden />
                <span>People</span>
              </UnstyledButton>
            </Menu.Target>
          </Tooltip>
          {dropdown}
        </Menu>
      ) : (
        <Menu position="bottom-start" disabled={menuOff}>
          {/* Live's collage opens the menu; ours is a button under the people, beside each
              person's own remove, never around it, so a remove stays usable with every seat
              taken (the menu's button is off then). */}
          <div className={classes.tileFilled}>
            <Menu.Target>
              <UnstyledButton
                className={`${classes.tile} ${classes.collageTarget}`}
                aria-label="People"
                disabled={menuOff}
              />
            </Menu.Target>
            <span className={classes.collage}>
              {taken.map((seat) => (
                <Person
                  key={seat}
                  teamId={teamId}
                  brandId={brandId}
                  seat={seat}
                  source={seats[seat]}
                  making={making.includes(seat)}
                  failed={failed.includes(seat)}
                  pictures={pictures}
                  disabled={disabled}
                  onRetry={onRetry === null ? null : () => onRetry(seat)}
                  onRemove={() => onRemove(seat)}
                />
              ))}
            </span>
          </div>
          {dropdown}
        </Menu>
      )}
      {choosing ? (
        <AddActorsDialog
          teamId={teamId}
          brandId={brandId}
          chosen={[]}
          max={free.length}
          onClose={() => setChoosing(false)}
          onConfirm={(actors) => {
            onPick(
              actors.flatMap((actor, at) => {
                const seat = free[at];
                return seat === undefined ? [] : [{ seat, actor }];
              }),
            );
            setChoosing(false);
          }}
        />
      ) : null}
    </div>
  );
}

function Person(props: {
  teamId: string;
  brandId: string;
  seat: Seat;
  source: ActorSource | null;
  making: boolean;
  failed: boolean;
  pictures: PickedPictures;
  disabled: boolean;
  onRetry: (() => void) | null;
  onRemove: () => void;
}) {
  const { teamId, brandId, seat, source, making, failed, pictures, disabled, onRetry, onRemove } = props;
  const read = useRefPicture(teamId, brandId, source);
  const picked = source === null ? undefined : pictures[sourceKey(source)];
  const picture = picked?.url ?? read.still;
  const label = seat.toUpperCase();
  let shown;
  if (failed) {
    shown = (
      <>
        <ImageOff size={18} strokeWidth={1.5} aria-hidden />
        <VisuallyHidden>Making {label} failed</VisuallyHidden>
        {disabled || onRetry === null ? null : (
          <ActionIcon
            variant="subtle"
            size="sm"
            className={classes.personRetry}
            aria-label={`Make ${label} again`}
            onClick={onRetry}
          >
            <RotateCcw size={14} aria-hidden />
          </ActionIcon>
        )}
      </>
    );
  }
  else if (making || picture === null) shown = <Loader size="xs" aria-label={`Making ${label}`} />;
  else shown = <Image src={picture} alt="" className={classes.personPicture} />;
  return (
    <span className={classes.person} aria-label={label}>
      {shown}
      {/* Live's `isRemovable`: not while the form is locked, nor while its AI Actor is made. */}
      {disabled || making ? null : (
        <CloseButton
          size="xs"
          className={classes.tileRemove}
          aria-label="Remove person"
          onClick={onRemove}
        />
      )}
    </span>
  );
}

/** Live's "Let AI choose" back to Auto cast (`media-row`), after the People tile in the row. */
export function LetAiChoose({ disabled, onChoose }: { disabled: boolean; onChoose: () => void }) {
  return (
    <Button
      variant="subtle"
      size="compact-sm"
      className={classes.castLink}
      disabled={disabled}
      leftSection={<Sparkles size={16} strokeWidth={1.5} />}
      onClick={onChoose}
    >
      Let AI choose
    </Button>
  );
}
